import memoize from "memoizee";
import { memoizeGetUserDetails } from "./users";
import {
  getCourseSectionTopic,
  createCourseSectionTopic as createCourseSectionTopicAPI,
  updateCourseSectionTopic as updateCourseSectionTopicAPI,
  duplicateTopicContent as duplicateTopicContentAPI,
} from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopics";
import {
  updateCourseSection as updateCourseSectionAPI,
  getCourseSections,
  createCourseSection,
} from "@nualang/nualang-api-and-queries/APIs/CourseSections";
import { getCourseSectionTopicRoleplay } from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopicRoleplays";
import { batchCreateCourseSectionTopicPhrases as batchCreateCourseSectionTopicPhrasesAPI } from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopicPhrases";
import {
  createCourseSectionTopicPhraseList as createCourseSectionTopicPhraseListAPI,
} from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopicPhraseLists";
import { getImageUrl, getImageUrlWithSize } from "../imageDownload";
import {
  getCourses,
  createCourse,
  getCourseEnrolmentKey,
} from "@nualang/nualang-api-and-queries/APIs/Courses";
import {
  getBot,
  createBot as createBotAPI,
  getBots,
} from "@nualang/nualang-api-and-queries/APIs/Bots";
import {
  createRoleplay as createRoleplayAPI,
  getRoleplays as getRoleplaysAPI,
} from "@nualang/nualang-api-and-queries/APIs/Roleplays";
import {
  invalidateCache,
  DataType,
} from "@nualang/nualang-api-and-queries/APIs/invalidation";
import { addCourseToClassroom } from "./classrooms";
import config from "../../config";

import logger from "../../logger";

export const memoizeGetCourseSectionTopic = memoize(getCourseSectionTopic, {
  promise: true,
});

export const memoizeGetCourseSectionTopicRoleplay = memoize(
  getCourseSectionTopicRoleplay,
  { promise: true },
);

export const memoizeGetBot = memoize(getBot, { promise: true });

export const formatCourse = async (
  c,
  user,
  ignoreKey = false,
  { useResolvedCreator = false } = {},
) => {
  const { username, attributes } = user;
  const isCreator =
    c.createdBy === username ||
    (c.collaborators &&
      Array.isArray(c.collaborators) &&
      c.collaborators.some(
        (email) => email.toLowerCase() === attributes?.email?.toLowerCase(),
      ));
  if (isCreator && !ignoreKey) {
    // Enrolment key only exists if course is private and should only be accessible by creator or collaborator
    try {
      const enrolmentResponse = await getCourseEnrolmentKey(c.courseId);
      if (enrolmentResponse?.Item?.enrolmentKey) {
        c.enrolmentKey = enrolmentResponse.Item.enrolmentKey || "";
      }
    } catch (err) {
      logger.error(err);
    }
  }
  // Search results arrive with their creator already resolved by one batched
  // lookup; only fall back to a per-creator request when it isn't.
  const creatorFromSearch = useResolvedCreator && c.creatorResolved;
  const userDetails = creatorFromSearch
    ? null
    : await memoizeGetUserDetails(c.createdBy);
  const userImage = creatorFromSearch
    ? c.userImage || null
    : userDetails && userDetails.UserAttributes
      ? userDetails.UserAttributes.picture
      : null;
  const verificationStatus = creatorFromSearch
    ? c.verificationStatus || null
    : userDetails && userDetails.verificationStatus
      ? userDetails.verificationStatus
      : null;
  const gameAward = creatorFromSearch
    ? c.gameAward || null
    : userDetails && userDetails.gameAward
      ? userDetails.gameAward
      : null;
  const createdByName = creatorFromSearch
    ? c.createdByName || null
    : userDetails && userDetails.UserAttributes
      ? userDetails.UserAttributes.preferred_username
      : null;
  let pictureXs = null;
  let cardPicture = null;
  let bannerXs = null;
  const isProdImgOnDev =
    c && c.picture && c.picture.includes("prod-1") && config.stage === "dev"
      ? true
      : false;
  if (c.pictureS3Key && !isProdImgOnDev) {
    pictureXs = getImageUrl(c.pictureS3Key, {
      resize: {
        width: 25,
      },
    });
    cardPicture = getImageUrl(c.pictureS3Key, {
      resize: {
        width: 600,
      },
    });
  } else if (
    c.picture &&
    c.picture.includes("nualang.com/") &&
    c.picture.includes("images") &&
    !isProdImgOnDev
  ) {
    pictureXs = getImageUrlWithSize(c.picture, null, 25);
    cardPicture = getImageUrlWithSize(c.picture, null, 600);
  }
  // Set cardPicture and pictureXs to null if the default card is being used
  if (!c.picture) {
    cardPicture = null;
    pictureXs = null;
  }

  if (c.picture && c.picture.includes(config.OldImageCreatorURL)) {
    const path = c.picture.split(config.OldImageCreatorURL)[1];
    c.picture = `${config.API}/image-creator${path}`;
  }

  if (c.bannerS3Key) {
    bannerXs = getImageUrl(c.bannerS3Key, {
      resize: {
        width: 25,
      },
    });
  } else if (
    c.banner &&
    c.banner.includes("nualang.com/") &&
    c.banner.includes("images")
  ) {
    bannerXs = getImageUrlWithSize(c.banner, null, 25);
  }

  if (!c.banner) {
    bannerXs = null;
  }

  return {
    ...c,
    isCreator,
    userImage,
    verificationStatus,
    gameAward,
    createdByName,
    pictureXs,
    cardPicture,
    bannerXs,
  };
};

export const formatCourses = (courses = [], user, options) =>
  Promise.all(courses.map((c) => formatCourse(c, user, true, options)));

export const formatTopic = async (t, user) => {
  const { username } = user;
  const userDetails = await memoizeGetUserDetails(t.createdBy);
  const userImage =
    userDetails && userDetails.UserAttributes
      ? userDetails.UserAttributes.picture
      : null;
  const preferred_username =
    userDetails && userDetails.UserAttributes
      ? userDetails.UserAttributes.preferred_username
      : null;
  const verificationStatus =
    userDetails && userDetails.UserAttributes
      ? userDetails.verificationStatus
      : null;
  const gameAward =
    userDetails && userDetails.UserAttributes ? userDetails.gameAward : null;
  const isCreator = t.createdBy === username;
  let pictureXs = null;
  let cardPicture = null;
  let bannerXs = null;
  if (t.pictureS3Key) {
    pictureXs = getImageUrl(t.pictureS3Key, {
      resize: {
        width: 25,
      },
    });
    cardPicture = getImageUrl(t.pictureS3Key, {
      resize: {
        width: 600,
      },
    });
  } else if (t.picture && t.picture.includes("nualang.com/")) {
    pictureXs = getImageUrlWithSize(t.picture, null, 25);
    cardPicture = getImageUrlWithSize(t.picture, null, 600);
  }

  if (t.bannerS3Key) {
    bannerXs = getImageUrl(t.bannerS3Key, {
      resize: {
        width: 25,
      },
    });
  } else if (
    t.banner &&
    t.banner.includes("nualang.com/") &&
    t.banner.includes("images")
  ) {
    bannerXs = getImageUrlWithSize(t.banner, null, 25);
  }

  return {
    ...t,
    userImage,
    verificationStatus,
    gameAward,
    createdByName: preferred_username,
    isCreator,
    pictureXs,
    cardPicture,
    phrases: [...(t.phrases || [])].sort((a, b) => a.idx - b.idx),
    bannerXs,
  };
};

export const formatTopics = (topics = [], user) =>
  Promise.all(topics.map((t) => formatTopic(t, user)));

export const fetchClassroomCourses = async (courseIds, user) => {
  try {
    if (courseIds && Array.isArray(courseIds) && courseIds.length > 0) {
      const courses = await getCourses({
        courseIds: courseIds.toString(),
        verbose: true,
        limit: config.limits.courses,
      });
      if (courses.errorMessage) {
        throw new Error(courses.errorMessage);
      }
      const formattedCourses = await formatCourses(courses.Items, user);
      return formattedCourses;
    }
    return [];
  } catch (error) {
    logger.error(error);
    return null;
  }
};

export const duplicateCourse = async (values) => {
  try {
    if (values.visibility === "private") {
      values.enrolmentKey = `${values.learnLang}${Math.floor(Math.random() * 999 + 100)}`;
    } else {
      values.enrolmentKey = `${values.learnLang}${Math.floor(Math.random() * 999 + 100)}`;
    }
  } catch (err) {
    logger.error(err);
  }
  const originalCourse = {
    courseId: values.courseId,
  };
  const sections = await getCourseSections(originalCourse.courseId, {
    limit: config.limits.courseSections,
  });

  const isCreator =
    values.createdBy === values.username ||
    (values.collaborators &&
      Array.isArray(values.collaborators) &&
      values.collaborators.includes(values?.attributes?.email));

  const duplicatedBy = isCreator ? "creator" : "community";

  let newCourse = {};

  const newCourseValues = {
    ...values,
    courseName: values.courseName + " (duplicate)",
    visibility: "private",
    collaborators: [],
    duplicated: true,
    duplicatedFrom: originalCourse.courseId,
    duplicatedBy: duplicatedBy,
    ...(values.classroomId && { classroomId: values.classroomId }),
  };
  delete newCourseValues.createdBy;
  delete newCourseValues.username;
  delete newCourseValues.userImage;
  delete newCourseValues.isCreator;
  delete newCourseValues.createdByName;
  delete newCourseValues.createdAt;
  delete newCourseValues.exerciseCompletionTotal;
  delete newCourseValues.exerciseStartTotal;

  newCourse = await createCourse(newCourseValues);

  const { courseId } = newCourse.Item;

  const sectionsPromises =
    sections &&
    sections.Items.map(async (s) => {
      const { sectionName, topics, idx } = s;
      const section = {
        sectionName: sectionName,
        idx: idx,
      };
      const newSection = await createCourseSection(courseId, section);
      const { sectionId } = newSection.Item;
      const topicsPromises =
        topics &&
        topics.map(async (tp) => {
          try {
            let topic = { ...tp, visibility: "private" };
            const newRolePlays = topic.roleplays.map((r) => {
              const roleplay = { ...r, visibility: "private" };
              return roleplay;
            });
            const newBots = topic.bots.map((b) => {
              const bot = { ...b, visibility: "private" };
              return bot;
            });
            topic = { ...topic, roleplays: newRolePlays, bots: newBots };
            const topicCreateResponse = await createCourseSectionTopicAPI(
              courseId,
              sectionId,
              topic,
            );
            await duplicateTopicContentAPI(tp, topicCreateResponse.Item);
          } catch (e) {
            logger.error(e);
          }
        });

      return Promise.all(topicsPromises);
    });

  await Promise.all(sectionsPromises);

  if (values.classroomId && newCourse.Item?.courseId) {
    await addCourseToClassroom(newCourse.Item.courseId, values.classroomId);
  }

  return {
    ...newCourse,
    classroomId: values.classroomId,
  };
};

/**
 * Duplicates a topic, cleans and recreates the roleplays, bots and phrases
 * associated with it
 */
export const duplicateTopic = async (
  values,
  roleplays = [],
  bots = [],
  context = "",
  isAddingToCourseSection = false,
) => {
  try {
    // Remove all null values recursively
    const removeNulls = (obj) => {
      if (Array.isArray(obj)) {
        return obj
          .map(removeNulls)
          .filter((value) => value !== null && value !== undefined);
      }

      if (obj && typeof obj === "object") {
        return Object.keys(obj).reduce((acc, key) => {
          const value = removeNulls(obj[key]);

          if (value !== null && value !== undefined) {
            acc[key] = value;
          }

          return acc;
        }, {});
      }

      return obj;
    };

    let topic = removeNulls(values);


    const [courseId, sectionId] = topic.courseSectionId.split("|");

    let roleplaysToCreate = [];
    let botsToCreate = [];
    let phraseListsToCreate = topic.phraseLists || [];

    /**
     * Bots and roleplays from ViewCourse screen only fetches minimal fields so
     * they need to be fetched from API before being recreated
     */
    if (context.toLowerCase() === "view-course") {
      const fetchedRoleplays = await getRoleplaysAPI({
        courseSectionTopicId: `${courseId}|${sectionId}|${topic.topicId}`,
        limit: config.limits.roleplays,
      });
      const fetchedBots = await getBots({
        courseSectionTopicId: `${courseId}|${sectionId}|${topic.topicId}`,
        limit: config.limits.bots,
      });

      roleplaysToCreate = fetchedRoleplays.Items;
      botsToCreate = fetchedBots.Items;
    } else if (context.toLowerCase() === "view-topic") {
      roleplaysToCreate = roleplays;
      botsToCreate = bots;
    }

    // Attributes to delete from current topic resources before getting recreated in duplicate
    const topicAttribtesToRemove = [
      "courseSectionId",
      "topicId",
      "createdBy",
      "createdByName",
      "createdAt",
      "updatedAt",
      "completions",
      "bots",
      "phrases",
      "phraseLists",
      "roleplays",
      "exerciseCompletionTotal",
      "exerciseStartTotal",
    ];

    const phraseListAttributesToRemove = [
      "phraseListId",
      "courseSectionTopicId",
      "createdAt",
      "createdBy",
      "createdByName",
      "updatedAt",
      "phrases",
    ];

    const phraseAttributesToRemove = [
      "courseSectionTopicId",
      "phraseListId",
      "createdAt",
      "createdBy",
      "createdByName",
      "phraseId",
      "updatedAt",
    ];
    const roleplayAttributesToRemove = [
      "courseSectionTopicId",
      "createdAt",
      "createdBy",
      "createdByName",
      "roleplayId",
      "updatedAt",
      "username",
      "isCreator",
      "exerciseCompletionTotal",
      "exerciseStartTotal",
    ];
    const botAttributesToRemove = [
      "courseSectionTopicId",
      "createdAt",
      "createdBy",
      "createdByName",
      "botId",
      "updatedAt",
      "username",
      "isCreator",
      "exerciseCompletionTotal",
      "exerciseStartTotal",
    ];

    topicAttribtesToRemove.forEach((a) => delete topic[a]);
    topic.topicName = isAddingToCourseSection && isAddingToCourseSection === true ? topic.topicName : topic.topicName + " (Duplicate)";
    topic.idx += 1;
    topic.visibility = "private";

    const topicCreateResponse = await createCourseSectionTopicAPI(
      courseId,
      sectionId,
      topic,
    );
    const { topicId } = topicCreateResponse.Item;

    const duplicatedPhraseLists = await Promise.all(
      phraseListsToCreate.map(async (pl) => {
        const phraseList = { ...pl };
        const phrases = phraseList.phrases || [];

        if (!phraseList.description) {
          phraseList.description = "";
        }

        phraseListAttributesToRemove.forEach((a) => delete phraseList[a]);
        const phraseListResponse =
          await createCourseSectionTopicPhraseListAPI(
            courseId,
            sectionId,
            topicId,
            phraseList,
          );

        const newPhraseList = phraseListResponse.Item;

        const phrasesToCreate = phrases.map((p, i) => {
          const phrase = { ...p };

          phraseAttributesToRemove.forEach((a) => delete phrase[a]);

          return {
            ...phrase,
            courseSectionTopicId: `${courseId}|${sectionId}|${topicId}`,
            phraseListId: newPhraseList.phraseListId,
            idx: phrase.idx,
          };
        });

        const batchResponse =
          phrasesToCreate.length > 0
            ? await batchCreateCourseSectionTopicPhrasesAPI(
                courseId,
                sectionId,
                topicId,
                { phrases: phrasesToCreate },
              )
            : null;

        const createdPhrases =
          batchResponse?.Items ||
          batchResponse?.Item ||
          [];

        return {
          ...newPhraseList,
          phrases: createdPhrases,
        };
      }),
    );

    let roleplayApiCalls = roleplaysToCreate.map((r) => {
      let roleplay = { ...r };

      roleplayAttributesToRemove.forEach((a) => delete roleplay[a]);

      roleplay.courseSectionTopicId =
        `${courseId}|${sectionId}|${topicId}`;

      return createRoleplayAPI(roleplay);
    });


    let botApiCalls = botsToCreate.map((b) => {
      let bot = { ...b };

      botAttributesToRemove.forEach((a) => delete bot[a]);

      bot.courseSectionTopicId =
        `${courseId}|${sectionId}|${topicId}`;

      return createBotAPI(bot);
    });

    const responses = await Promise.all([
      Promise.all(roleplayApiCalls),
      Promise.all(botApiCalls),
    ]);

    await invalidateCache(DataType.TOPIC_DUPLICATE, {
      courseId,
      sectionId,
      topicId,
    });

    return {
      ...topicCreateResponse.Item,
      phraseLists: duplicatedPhraseLists,
      roleplays: (responses[0] || []).map((r) => r.Item),
      bots: (responses[1] || []).map((r) => r.Item),
    };
  } catch (e) {
    logger.error(e);
  }
  return false;
};

export const duplicatePhraseList = async (
  phraseList,
  courseId,
  sectionId,
  topicId,
) => {
  try {
    const phraseListAttributesToRemove = [
      "phraseListId",
      "createdAt",
      "createdBy",
      "createdByName",
      "updatedAt",
      "phrases",
    ];

    const phraseAttributesToRemove = [
      "phraseListId",
      "createdAt",
      "createdBy",
      "createdByName",
      "phraseId",
      "updatedAt",
    ];

    const cleanedPhraseList = { ...phraseList };
    const phrases = cleanedPhraseList.phrases || [];

    if (!cleanedPhraseList.description) {
      cleanedPhraseList.description = "";
    }

    phraseListAttributesToRemove.forEach((a) => {
      delete cleanedPhraseList[a];
    });

    cleanedPhraseList.phraseListName = `${cleanedPhraseList.phraseListName} (Duplicate)`;

    const phraseListResponse =
      await createCourseSectionTopicPhraseListAPI(
        courseId,
        sectionId,
        topicId,
        cleanedPhraseList,
      );

    const newPhraseList = phraseListResponse.Item;

    const phrasesToCreate = phrases.map((p) => {
      const phrase = { ...p };

      phraseAttributesToRemove.forEach((a) => delete phrase[a]);

      return {
        ...phrase,
        courseSectionTopicId: `${courseId}|${sectionId}|${topicId}`,
        phraseListId: newPhraseList.phraseListId,
        idx: phrase.idx,
      };
    });

    const batchResponse =
      phrasesToCreate.length > 0
        ? await batchCreateCourseSectionTopicPhrasesAPI(
            courseId,
            sectionId,
            topicId,
            { phrases: phrasesToCreate },
          )
        : null;

    const createdPhrases =
      batchResponse?.Items ||
      batchResponse?.Item ||
      [];

    return {
      ...newPhraseList,
      phrases: createdPhrases,
    };

  } catch (e) {
    logger.error(e);
    return false;
  }
};

export const updateCourseSectionTopics = async (
  courseId,
  sectionId,
  topics,
) => {
  const response = await Promise.all(
    topics.map(async (topic, i) => {
      await updateCourseSectionTopicAPI(courseId, sectionId, topic.topicId, {
        idx: i,
      });
    }),
  );
  return response;
};

export const updateCourseSections = async (courseId, sections) => {
  const response = await Promise.all(
    sections.map(async (section, i) => {
      await updateCourseSectionAPI(courseId, section.sectionId, {
        idx: i,
      });
    }),
  );
  return response;
};
export const DEFAULT_FILTERS = {
  visibility: "all",
  lang: "all",
  difficulty: "all",
  sort: "newest",
  search: "",
};

export function isDefaultFilters(filters = {}) {
  return Object.keys(DEFAULT_FILTERS).every(
    (key) => filters[key] === DEFAULT_FILTERS[key],
  );
}

export function getAvailableDifficulties(courses = []) {
  return new Set(courses.map((course) => course?.difficulty).filter(Boolean));
}

function getCourseCreatedAt(course) {
  return course?.createdAt || 0;
}

export function filterAndSortCourses(courses = [], filters = {}) {
  const {
    visibility = DEFAULT_FILTERS.visibility,
    lang = DEFAULT_FILTERS.lang,
    difficulty = DEFAULT_FILTERS.difficulty,
    sort = DEFAULT_FILTERS.sort,
    search = DEFAULT_FILTERS.search,
  } = filters;
  const searchText = search.trim().toLowerCase();

  const filtered = courses.filter((course) => {
    if (visibility !== "all" && course?.visibility !== visibility) return false;
    if (lang !== "all" && course?.learnLang !== lang) return false;
    if (difficulty !== "all" && course?.difficulty !== difficulty) return false;
    if (searchText) {
      const nameMatch = (course?.courseName || "").toLowerCase().includes(searchText);
      const descriptionMatch = (course?.description || "")
        .toLowerCase()
        .includes(searchText);
      if (!nameMatch && !descriptionMatch) return false;
    }
    return true;
  });

  const byNameAsc = (a, b) =>
    (a?.courseName || "").localeCompare(b?.courseName || "", undefined, {
      sensitivity: "base",
    });

  const sorted = [...filtered];
  if (sort === "oldest") {
    sorted.sort(
      (a, b) => getCourseCreatedAt(a) - getCourseCreatedAt(b) || byNameAsc(a, b),
    );
  } else if (sort === "alphabetical") {
    sorted.sort(byNameAsc);
  } else {
    sorted.sort(
      (a, b) => getCourseCreatedAt(b) - getCourseCreatedAt(a) || byNameAsc(a, b),
    );
  }

  return sorted;
}
